import React, { useState } from 'react';
import { 
  Modal, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  ButtonGroup
} from 'reactstrap';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';

interface QuickBugReportProps {
  isOpen: boolean;
  toggle: () => void;
}

const QuickBugReport: React.FC<QuickBugReportProps> = ({ isOpen, toggle }) => {
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!description.trim()) {
      setError('Bitte beschreiben Sie den Bug');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Collect browser info
      const browserInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        windowSize: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString()
      };

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('description', description.trim());
      formData.append('url', window.location.href);
      formData.append('browser_info', JSON.stringify(browserInfo));
      formData.append('priority', priority);
      
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const response = await axios.post(`${config.api.API_URL}/bug-reports`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        toast.success('Bug Report wurde erfolgreich gesendet!');
        setDescription('');
        setPriority('medium');
        setAttachment(null);
        setAttachmentPreview(null);
        toggle();
      }
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError('Fehler beim Senden des Bug Reports');
      
      // Save to session storage as fallback
      const bugReport = {
        description,
        priority,
        url: window.location.href,
        timestamp: new Date().toISOString()
      };
      sessionStorage.setItem('pendingBugReport', JSON.stringify(bugReport));
      
      toast.error('Bug Report konnte nicht gesendet werden. Er wurde lokal gespeichert.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Support Cmd+Enter on Mac and Ctrl+Enter on Windows/Linux
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e as any);
    }
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const isAllowedExtension = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'txt', 'xls', 'xlsx', 'doc', 'docx'].includes(fileExtension || '');
      
      if (!allowedTypes.includes(file.type) && !isAllowedExtension) {
        setError('Erlaubte Dateitypen: Bilder (JPEG, PNG, GIF, WebP), PDF, Text, Excel, Word');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Die Datei darf maximal 10MB groß sein');
        return;
      }
      
      setError(null);
      setAttachment(file);
      
      // Create preview only for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachmentPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreview(null);
      }
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    // Reset the file input
    const fileInput = document.getElementById('attachment-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggle}>
          <i className="ri-bug-line me-2"></i>
          Bug Report
        </ModalHeader>
        <ModalBody>
          {error && (
            <Alert color="danger" className="mb-3" fade={false}>
              {error}
            </Alert>
          )}
          
          <FormGroup>
            <Label for="description">
              Was ist passiert? <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              id="description"
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Bitte beschreiben Sie den Bug so genau wie möglich...
- Was haben Sie gemacht?
- Was ist passiert?
- Was hätte passieren sollen?
- Können Sie den Fehler reproduzieren?"
              disabled={isSubmitting}
              autoFocus
            />
            <small className="text-muted d-block">
              Tipp: Drücken Sie {navigator.platform.indexOf('Mac') > -1 ? 'Cmd' : 'Strg'}+Enter zum Absenden
            </small>
            <small className="text-muted d-block mt-1">
              <i className="ri-code-line me-1"></i>
              Console-Errors und Code werden automatisch erkannt und formatiert dargestellt.
            </small>
          </FormGroup>

          <FormGroup>
            <Label>Priorität</Label>
            <div>
              <ButtonGroup>
                <Button
                  color={priority === 'low' ? 'success' : 'light'}
                  onClick={() => setPriority('low')}
                  disabled={isSubmitting}
                >
                  <i className="ri-arrow-down-line me-1"></i>
                  Niedrig
                </Button>
                <Button
                  color={priority === 'medium' ? 'warning' : 'light'}
                  onClick={() => setPriority('medium')}
                  disabled={isSubmitting}
                >
                  <i className="ri-subtract-line me-1"></i>
                  Mittel
                </Button>
                <Button
                  color={priority === 'high' ? 'danger' : 'light'}
                  onClick={() => setPriority('high')}
                  disabled={isSubmitting}
                >
                  <i className="ri-arrow-up-line me-1"></i>
                  Hoch
                </Button>
              </ButtonGroup>
            </div>
            <small className="text-muted">
              Hoch = Blockiert die Arbeit, Mittel = Störend aber nicht blockierend, Niedrig = Kosmetisch
            </small>
          </FormGroup>

          <FormGroup>
            <Label for="attachment">
              Datei anhängen (optional)
            </Label>
            <div>
              {!attachment ? (
                <Input
                  type="file"
                  id="attachment-input"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,.pdf,.txt,.xls,.xlsx,.doc,.docx"
                  onChange={handleAttachmentChange}
                  disabled={isSubmitting}
                />
              ) : (
                <div className="attachment-preview-container">
                  <div className="position-relative d-inline-block">
                    {attachmentPreview ? (
                      <img 
                        src={attachmentPreview} 
                        alt="Attachment Preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px',
                          border: '1px solid #dee2e6',
                          borderRadius: '0.25rem'
                        }}
                      />
                    ) : (
                      <div className="p-4 bg-light border rounded text-center">
                        <i className="ri-file-line ri-3x text-muted mb-2"></i>
                        <div className="text-muted">
                          {attachment?.name}
                        </div>
                      </div>
                    )}
                    <Button
                      color="danger"
                      size="sm"
                      className="position-absolute top-0 end-0 m-1"
                      onClick={removeAttachment}
                      disabled={isSubmitting}
                    >
                      <i className="ri-close-line"></i>
                    </Button>
                  </div>
                  <div className="mt-2">
                    <small className="text-muted">
                      {attachment?.name} ({((attachment?.size || 0) / 1024 / 1024).toFixed(2)} MB)
                    </small>
                  </div>
                </div>
              )}
            </div>
            <small className="text-muted">
              Unterstützte Formate: Bilder (JPEG, PNG, GIF, WebP), PDF, Text, Excel, Word (max. 10MB)
            </small>
          </FormGroup>

          <div className="mt-3 p-3 bg-light rounded">
            <h6 className="mb-2">Automatisch erfasste Informationen:</h6>
            <small className="text-muted">
              <div>📍 URL: {window.location.href}</div>
              <div>🌐 Browser: {navigator.userAgent.split(' ').slice(-2).join(' ')}</div>
              <div>📱 Bildschirm: {window.screen.width}x{window.screen.height}</div>
              <div>🕐 Zeit: {new Date().toLocaleString('de-DE')}</div>
            </small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button 
            color="primary" 
            type="submit" 
            disabled={isSubmitting || !description.trim()}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Wird gesendet...
              </>
            ) : (
              <>
                <i className="ri-send-plane-line me-2"></i>
                Bug Report senden
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default QuickBugReport;